// REQ-2026-11-02-6200: move a user who is already signed in to V5.1 over to V6 on an ordinary
// page load, not only at sign-in.
//
// The API decides. On a few V1 page GETs (the header loader, Dashboard, Calendar, My Account,
// Manager, the admin Users/Duties/Schedules/Settings tabs) it adds `redirect_url` when this user
// should be on V6. It leaves the key off for a root-IP caller (so V5.1 stays usable for
// debugging), a time clock, an install with no V6 URL and a user who must change their password.
// The list and the rules are in the API (front_helper v6_redirect_url / v6_redirect_routes).
// This file only follows the URL.
//
// Every V5.1 call goes through the default axios instance (actions/REST.js, API.js,
// GlobalActions.js, APIManager.js; there is no axios.create() in src/), so one response
// interceptor sees all of them. superAttest.js is the precedent.
//
// What it deliberately does NOT follow:
//   - anything but a GET. A save must never navigate away. The API does not send the key on a
//     write either; this is belt and braces;
//   - /auth/ calls. Login.js:82 handles the sign-in response itself, after it has checked
//     force_pw_change and the REQ-5401 super attestation. Acting here first would skip both;
//   - the sign-in, reset and form routes (/login, /reset, /reset_password, /form). None of them
//     make a listed call, so this is belt and braces too.
// It follows the URL once. The header and the tab content load in parallel, so two responses
// can both carry it.

import axios from 'axios';

let redirecting = false;

const NO_REDIRECT_ROUTES = /\/(login|reset_password|reset|form)(\/|$)/;

export function bindV6Redirect() {
  // Back from V6 can restore this page from the browser's back/forward cache: the JS state
  // comes back as it was (redirecting=true, the tab half-rendered behind a promise that never
  // settled) and no API call runs, so nothing finds out the key is gone. V6's sign-out now
  // deletes that same key on the server, so reload instead: the header call then answers 401,
  // Header.js drops the cookie and the user gets the sign-in page. A key that is still valid
  // (they did not sign out of V6) reloads into the same redirect as before.
  window.addEventListener('pageshow', event => {
    if (event.persisted && redirecting) window.location.reload();
  });
  axios.interceptors.response.use(response => {
    const url = response && response.data && response.data.redirect_url;
    if (!url || redirecting) return response;
    const config = response.config || {};
    const method = (config.method || 'get').toLowerCase();
    const isAuth = /\/auth\//i.test(config.url || '');
    const onExemptRoute = NO_REDIRECT_ROUTES.test(window.location.pathname || '');
    if (method === 'get' && !isAuth && !onExemptRoute) {
      redirecting = true;
      window.location.assign(url);
      // Never settle, so the page does not go on rendering behind the navigation.
      return new Promise(() => {});
    }
    return response;
  });
}
